import { Dictionary } from 'lodash'

const pool: Dictionary<Point> = {}

export default class Point {
  static key(x: number, y: number) {
    return `${x},${y}`
  }

  static list(...xys: number[]) {
    if (xys.length % 2 !== 0)
      throw new Error('Odd number of arguments cannot be sent to create a list of points.')

    const list: Point[] = []
    for (let i = 0; i < xys.length; i++)
      list.push(pt(xys[i], xys[++i]))

    return list
  }

  constructor(readonly x: number, readonly y: number) {
    const key = Point.key(x, y)
    if (pool[key]) return pool[key]
    pool[key] = this
  }

  translate(delta: Point): Point
  translate(dx: number, dy: number): Point
  translate(dxOrDelta: number | Point, dy?: number) {
    if (dxOrDelta instanceof Point) {
      const delta = dxOrDelta
      return new Point(this.x + delta.x, this.y + delta.y)
    } else {
      const dx = dxOrDelta
      return new Point(this.x + dx, this.y + dy!)
    }
  }

  translateX(dx: number) {
    return new Point(this.x + dx, this.y)
  }

  translateY(dy: number) {
    return new Point(this.x, this.y + dy)
  }

  inRect(x: number, y: number, width: number, height: number) {
    return this.x > x && this.y > y && this.x < x + width && this.y < y + height
  }

  toSign() {
    return pt(this.x === 0 ? 0 : this.x / Math.abs(this.x), this.y === 0 ? 0 : this.y / Math.abs(this.y))
  }

  scalarMultiply(n: number) {
    return pt(this.x * n, this.y * n)
  }

  abs() {
    return pt(Math.abs(this.x), Math.abs(this.y))
  }

  round() {
    return pt(Math.round(this.x), Math.round(this.y))
  }

  floor() {
    return pt(Math.floor(this.x), Math.floor(this.y))
  }

  ceil() {
    return pt(Math.ceil(this.x), Math.ceil(this.y))
  }

  equals(pt: Point): boolean
  equals(x: number, y: number): boolean
  equals(xOrPoint: number | Point, y?: number) {
    if (xOrPoint instanceof Point) {
      const point = xOrPoint
      return this.x === point.x && this.y === point.y
    } else {
      const x = xOrPoint
      return this.x === x && this.y === y!
    }
  }

  distanceFrom(pt: Point): number
  distanceFrom(x: number, y: number): number
  distanceFrom(pt: Point | number, y?: number) {
    if (pt instanceof Point) {
      return Math.abs(this.x - pt.x) + Math.abs(this.y - pt.y)
    } else {
      return Math.abs(this.x - pt) + Math.abs(this.y - y!)
    }
  }

  static fromKey(key: string) {
    const arr = key.split(',')
    return new Point(parseInt(arr[0], 10), parseInt(arr[1], 10))
  }

  get key() {
    return Point.key(this.x, this.y)
  }

  toString() {
    return `x=${this.x}, y=${this.y}`
  }

  convert<T extends Point>(type: any): T {
    return new type(this)
  }
}

export function pt(x: number, y: number) {
  return new Point(x, y)
}
